import { useContext, useMemo } from 'react';

import dayjs from 'dayjs';

import { OptimizationContainer } from '@cast/types';

import { getContainerResourceRecommendations } from 'features/workload-optimization';

import { nonPredictionMetricKeys, ResourceMetricKey } from './_constants';
import { prepareCpuHistoricalChartMetrics } from './_utils';
import { useIsPsiEnabled } from '../../../_hooks/permissions/useIsPsiEnabled';
import { useDisabledMetricsBoxes } from '../../../_hooks/useDisabledMetricsBoxes';
import { WorkloadDetailsContext } from '../../../workload-details/_context';

export const useCpuChartData = ({
  container,
  timeZone,
}: {
  container?: OptimizationContainer;
  timeZone: string;
}) => {
  const isPsiEnabled = useIsPsiEnabled();
  const { workload, metrics, isLoading, isFetching, error, refetch } =
    useContext(WorkloadDetailsContext);
  const { toggleMetric, isMetricDisabled } =
    useDisabledMetricsBoxes<ResourceMetricKey>();

  const containerMetrics = useMemo(
    () => metrics?.containers.find(({ name }) => name === container?.name),
    [metrics?.containers, container?.name]
  );

  const resourceRecommendations = useMemo(
    () => (container ? getContainerResourceRecommendations(container) : null),
    [container]
  );

  const chartData = useMemo(() => {
    if (!workload || !container || !resourceRecommendations) {
      return null;
    }

    const { cpu } = resourceRecommendations;

    const cpuCoresWithStall = containerMetrics?.cpuCores.map((v, i) => ({
      ...v,
      avgStall: containerMetrics.cpuStallPct?.[i]?.avg,
    }));

    const now = dayjs().utc();
    const cpuCoresHistorical = cpuCoresWithStall?.filter(
      ({ timestamp }) => !dayjs.utc(timestamp).isAfter(now)
    );

    const hasStall = isPsiEnabled && !!containerMetrics?.cpuStallPctAggregated;

    return {
      chartData: prepareCpuHistoricalChartMetrics(cpuCoresHistorical),
      chartDataKeys: hasStall
        ? nonPredictionMetricKeys
        : nonPredictionMetricKeys.filter((k) => k !== 'avgStall'),
      aggregated: containerMetrics?.cpuCoresAggregated && {
        ...containerMetrics.cpuCoresAggregated,
        avgStall: containerMetrics.cpuStallPctAggregated?.avg,
      },
      requested: cpu?.requested,
      recommended: cpu?.recommended,
      isReadOnly: workload.cpu?.isReadOnly,
      hasStall,
    };
  }, [
    workload,
    container,
    containerMetrics,
    resourceRecommendations,
    isPsiEnabled,
  ]);

  return {
    workload,
    timeZone,
    isLoading,
    isFetching,
    error,
    refetch,
    chartData,
    toggleMetric,
    isMetricDisabled,
  };
};
